import { h, VNode } from "preact";
import { Container, Row, Col, Button } from "preact-mui/lib";
import { useState, useEffect } from "preact/hooks";
import "./style.css";

interface Props {
  clubId: number;
}

interface ClubEventItem {
  eventItemId: number;
  name: string;
  arrivalTime: string;
  bookableSpots: number;
  bookablePerGuest?: number;
  eventId: number;
  eventName: string;
  eventStartDate: string;
  eventEndDate: string;
  eventAge?: number;
  eventDescription?: string;
  clubId: number;
}

interface UserInput {
  name: string;
  age: number;
  email: string;
  plusOnes?: number;
}

export default function App(props: Props): VNode {
  const emailRegExp = new RegExp(
    /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-ZåäöÅÄÖ]{2,}))$/
  );
  const nameRegExp: RegExp = /^[\p{L}\s']+$/u;

  const [clubEventItems, setClubEventItems] = useState<ClubEventItem[]>([]);
  const [clubEventItemFilteredDate, setClubEventItemFilteredDate] = useState<
    ClubEventItem[]
  >([]);
  const [filteredClubEventItems, setFilteredClubEventItems] = useState<
    ClubEventItem[]
  >([]);
  const [selectedEventItem, setSelectedEventItem] = useState<
    ClubEventItem | undefined
  >(undefined);
  const [userInput, setUserInput] = useState<UserInput | undefined>({
    name: "",
    age: 0,
    email: "",
  });
  const [currentStep, setCurrentStep] = useState(1);
  
  const sortByStartTime = (a: ClubEventItem, b: ClubEventItem): number => {
    const timeA = new Date(a.eventStartDate).getTime();
    const timeB = new Date(b.eventStartDate).getTime();
    return timeA - timeB;
  };


  // Error messages
  const [showNameError, setShowNameError] = useState<boolean>(false);
  const [showAgeError, setShowAgeError] = useState<boolean>(false);
  const [showEmailError, setShowEmailError] = useState<boolean>(false);
  const [showAcceptedTermsError, setShowAcceptedTermsError] =
    useState<boolean>(false);

  useEffect(() => {
    console.log("selectedEventItem", selectedEventItem)
  }, [selectedEventItem]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const clubEventItems = await fetchClubEventItems();
        setClubEventItems(clubEventItems);
      } catch (error) {
        if (error.response && error.response.status === 404) {
          console.log("Error 404: Club Event Items not found");
        } else {
          console.log("Error occurred while fetching Club Event Items:", error);
        }
      }
    };
    props.clubId && fetchData();
  }, []);

  useEffect(() => {
    if (!selectedEventItem) {
      console.log("not found selectedEventItem --> setting first")
      setSelectedEventItem(clubEventItemFilteredDate[0]);
    }
  },[clubEventItemFilteredDate])

  useEffect(() => {
    setFilteredClubEventItems(
      clubEventItems.filter(
        (item) =>
          item.eventStartDate.slice(0, 10) ===
          selectedEventItem.eventStartDate.slice(0, 10)
      )
    );
  }, [selectedEventItem])

  useEffect(() => {
    clubEventItems.length !== 0 && onChangeDay(clubEventItems.sort(sortByStartTime)[0].eventItemId);
    const handledEventItems = 
      clubEventItems.filter(
        (item: ClubEventItem, index: number, self: ClubEventItem[]) =>
          index ===
          self.findIndex(
            (t) =>
              t.eventStartDate.slice(0, 10) === item.eventStartDate.slice(0, 10)
          )
      ).sort(sortByStartTime);
    setClubEventItemFilteredDate(handledEventItems);
  }, [clubEventItems]);

  // API
  //const BASE_URL = "https://nightli-staging-server.link";
  //const BASE_URL = 'http://127.0.0.1:3000';
  const BASE_URL = 'https://nightli-prod-server.link';

  const fetchOptions: RequestInit = {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      "api-target": "rest",
    },
    mode: "cors",
  };

  const fetchClubEventItems = async (): Promise<ClubEventItem[]> => {
    return fetch(BASE_URL + "/public-guest-lists/" + props.clubId, fetchOptions)
      .then((res) => {
        return res.json();
      })
      .then((result) => result);
  };

  const sendPostPublicGuestListMutation = async (): Promise<void> => {
    const requestBody = {
      eventId: selectedEventItem.eventId,
      eventItemId: selectedEventItem.eventItemId,
      name: userInput.name,
      email: userInput.email,
      plusOnes: userInput.plusOnes ?? 0,
      age: userInput.age,
    };

    console.log("requestBody sent", requestBody)

    const options: RequestInit = {
      method: "POST",
      mode: "cors",
      headers: {
        "Content-Type": "application/json",
        "Access-Control-Request-Headers": "api-target",
        "api-target": "rest",
      },
      body: JSON.stringify(requestBody),
    };

    try {
      const response = await fetch(
        BASE_URL + "/public-guest-lists/" + props.clubId,
        Object.assign({}, fetchOptions, options)
      );
      const result = await response.json();
      if (result.error && result.error.statusCode === 403) {
        setCurrentStep(4)
      }
    } catch (error) {
      console.error(error);
    }
  };

  // LOGIC

  const moveToStep = (step: number) => {
    setShowNameError(false);
    setShowAgeError(false);
    setShowEmailError(false);
    setShowAcceptedTermsError(false);

    if (step === 2) {
      if (!userInput.name || !nameRegExp.test(userInput.name)) {
        setShowNameError(true);
        step = 1;
      }
    } else if (step == 3) {
      if (!userInput.email || !emailRegExp.test(userInput.email)) {
        setShowEmailError(true);
        step = 2;
      } else if (!userInput.age || userInput.age < 0 || userInput.age > 99) {
        setShowAgeError(true);
        step = 2;
      } else {
        onSubmit();
      }
    }
    setCurrentStep(step);
  };

  const onChangeDay = (selectedEventItemId: number) => {
    const selectedClubEventItem = clubEventItems.find(
      (ei) => ei.eventItemId == selectedEventItemId
    );
    const filteredEventItems = clubEventItems.filter(
      (item) =>
        item.eventStartDate.slice(0, 10) ===
        selectedClubEventItem.eventStartDate.slice(0, 10)
    );
    setFilteredClubEventItems(filteredEventItems);
    setSelectedEventItem(filteredEventItems[0]);
  };

  const onChangeClubEventItem = (selectedEventItemId: number) => {
    const selectedClubEventItem = clubEventItems.find(
      (ei) => ei.eventItemId == selectedEventItemId
    );
    setSelectedEventItem(selectedClubEventItem);
  };

  const checkPublicGuestListRequest = () => {
    return !showAgeError && !showEmailError && !showNameError;
  };

  const formatedEventDay = (clubEventItem: ClubEventItem): string => {
    return new Date(
      Date.parse(clubEventItem.eventStartDate)
    ).toLocaleDateString("en-GB", {
      weekday: "long",
      day: "numeric",
      month: "short",
      timeZone: "UTC"
    });
  };

  const formatedGuestlistAndArrivalTime = (
    clubEventItem: ClubEventItem
  ): string => {
    const eventItemArrivalTime = new Date(
      Date.parse(
        clubEventItem.arrivalTime.substring(
          0,
          clubEventItem.arrivalTime.length - 1
        )
      )
    );
    
    //Quickfix: Add one hour to the date for UTC +02:00  (A library should be used to for adjusting UTC time to SE time)
    eventItemArrivalTime.setHours(eventItemArrivalTime.getHours() + 1);
    
    const formattedTime = eventItemArrivalTime.toLocaleTimeString([], {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
    });
    const eventItemName = clubEventItem.name.substring(
      "(Web) ".length,
      clubEventItem.name.length
    );
    return eventItemName + " " + formattedTime;
  };

  const onSubmit = async () => {
    if (!checkPublicGuestListRequest()) {
      return;
    }
    sendPostPublicGuestListMutation();
  };
  

  return (
    <div className="body">
      {currentStep == 1 && (
        <Container style={{ padding: "10px" }}>
          <div id="centerInputs">
            <div className="container">
              <div style={{textAlign: 'left'}}>
                <div className="mui--text-subhead">Select day</div>
                <select
                  id="coloredInputLight"
                  style={{ width: 300 }}
                  onChange={(e) =>
                    onChangeDay(+(e.target as HTMLOptionElement).value)
                  }
                >
                  {clubEventItemFilteredDate.length === 0 ? (
                    <option value="no-events">No events</option>
                  ) : (
                    clubEventItemFilteredDate.map((options) => (
                      <option
                        key={options.eventItemId}
                        value={options.eventItemId}
                      >
                        {formatedEventDay(options)}
                      </option>
                    ))
                  )}
                </select>
              </div>

              <div style={{textAlign: 'left'}}>
              <div className="mui--text-subhead">Select guestlist or table</div>
              <select
                id="coloredInputLight"
                style={{ width: 300 }}
                onChange={(e) =>
                  onChangeClubEventItem(+(e.target as HTMLOptionElement).value)
                }
              >
                {filteredClubEventItems.length === 0 ? (
                  <option value="no-events">No guestlists or tables</option>
                ) : (
                  filteredClubEventItems.map((options) => (
                    <option key={options.eventItemId} value={options.eventItemId}>
                      {formatedGuestlistAndArrivalTime(options)}
                    </option>
                  ))
                )}
              </select>
              </div>
                    
              <div style={{textAlign: 'left'}}>
                <div className="mui--text-subhead">
                  Write your name + friends
                </div>

                <div class="row">
                  <span class="elements">
                    <input
                      type="text"
                      id="coloredInputDark"
                      style={{ width: 220 }}
                      onChange={(e) =>
                        setUserInput({
                          ...userInput,
                          name: (e.target as HTMLInputElement).value.trim(),
                        })
                      }
                    />
                  </span>

                  <span class="elements">
                    <select
                      id="coloredInputLight"
                      onChange={(e) =>
                        setUserInput({
                          ...userInput,
                          plusOnes: +(e.target as HTMLOptionElement).value,
                        })
                      }
                    >
                      <option value={0} selected>
                        +0
                      </option>
                      {selectedEventItem?.bookablePerGuest != null && selectedEventItem.bookablePerGuest < 10
                        ? Array.from({ length: selectedEventItem.bookablePerGuest }, (_, i) => i + 1).map((value) => (
                            <option key={value} value={value}>
                              +{value}
                            </option>
                          ))
                        : Array.from({ length: 10 }, (_, i) => i + 1).map((value) => (
                            <option key={value} value={value}>
                              +{value}
                            </option>
                          ))}
                    </select>
                  </span>
                </div>

                {showNameError && (
                  <div className="mui--text-body2" id="errorText">
                    Please enter a first and last name
                  </div>
                )}
              </div>

              <Button
                color="danger"
                id="button"
                onClick={() => {
                  clubEventItems.length > 0 && moveToStep(2);
                }}
              >
                Next Step
              </Button>

              <img
                src="https://uploads-ssl.webflow.com/604e5bf6813f947d0e60c30d/621b704ca8b443366a377c7d_nightli%20vit-09.svg"
                width="150"
                height="100"
                loading="lazy"
                alt="nightli my social nightlife app"
              />
            </div>
          </div>
        </Container>
      )}

      {currentStep == 2 && (
        <Container style={{ padding: "10px" }}>
          <div className="container">
            <div style={{textAlign: 'left'}}>
            <div className="mui--text-subhead">Email address</div>
            <input
              type="text"
              id="coloredInputDark"
              style={{width: 300}}
              onChange={(e) =>
                setUserInput({
                  ...userInput,
                  email: (e.target as HTMLInputElement).value.trim(),
                })
              }
            />

            {showEmailError && (
              <div className="mui--text-body2" id="errorText">
                Please enter a valid email address
              </div>
            )}
            </div>
            <div style={{textAlign: 'left'}}>
                <div className="mui--text-subhead">Enter age</div>
                <input
                  type="number"
                  id="ageInput"
                  onChange={(e) =>
                    setUserInput({
                      ...userInput,
                      age: +(e.target as HTMLInputElement).value,
                    })
                  }
                />

                {showAgeError && (
                  <div className="mui--text-body2" id="errorText">
                    Please enter a valid age
                  </div>
                )}
              </div>
            <Button
              color="danger"
              id="button"
              onClick={() => {
                moveToStep(3);
              }}
            >
              Request guestlist
            </Button>
            <div>
              <div className="mui--text-caption" id="noteOne">
                By tapping Request guestlist, your form will be sent to Nightli,
                and data will be saved and shared with a third-party business.
              </div>
              <div className="mui--text-caption" id="noteOne">
                By tapping Request guestlist you Agree to our{" "}
                <a href="https://app.nightli.se/all-terms-conditions" id="noteTwo">Terms</a>.
              </div>
            </div>
            <img
              src="https://uploads-ssl.webflow.com/604e5bf6813f947d0e60c30d/621b704ca8b443366a377c7d_nightli%20vit-09.svg"
              width="150"
              height="100"
              loading="lazy"
              alt="nightli my social nightlife app"
              class="logo-footer gh"
            />
          </div>
        </Container>
      )}

      {currentStep == 3 && (
        <Container style={{ padding: "10px" }}>
          <div className="container">
          <div className="mui--text-subhead" id="errorText">
            Request sent!
          </div>

          <div className="mui--text-subhead">Whats next?</div>

          <div className="mui--text-subhead" id="bgWaiting">
            Wait until you recieve an email with a reply...
          </div>

          <div className="mui--text-subhead">Find more events:</div>
          <div>
          <div class="row">
            <a
              href="https://play.google.com/store/apps/details?id=se.nightli"
              target="_blank"
              class="w-inline-block"
            >
              <img
                src="https://uploads-ssl.webflow.com/604e5bf6813f947d0e60c30d/604e5d49a8cd2b6f3ac853d8_google-download.png"
                loading="lazy"
                width="150"
                sizes="(max-width: 166px) 100vw, (max-width: 767px) 200.0077362060547px, (max-width: 991px) 25vw, 205.00001525878906px"
                srcset="https://uploads-ssl.webflow.com/604e5bf6813f947d0e60c30d/604e5d49a8cd2b6f3ac853d8_google-download-p-500.png 500w, https://uploads-ssl.webflow.com/604e5bf6813f947d0e60c30d/604e5d49a8cd2b6f3ac853d8_google-download-p-800.png 800w, https://uploads-ssl.webflow.com/604e5bf6813f947d0e60c30d/604e5d49a8cd2b6f3ac853d8_google-download-p-1080.png 1080w, https://uploads-ssl.webflow.com/604e5bf6813f947d0e60c30d/604e5d49a8cd2b6f3ac853d8_google-download-p-1600.png 1600w, https://uploads-ssl.webflow.com/604e5bf6813f947d0e60c30d/604e5d49a8cd2b6f3ac853d8_google-download-p-2000.png 2000w, https://uploads-ssl.webflow.com/604e5bf6813f947d0e60c30d/604e5d49a8cd2b6f3ac853d8_google-download-p-2600.png 2600w, https://uploads-ssl.webflow.com/604e5bf6813f947d0e60c30d/604e5d49a8cd2b6f3ac853d8_google-download.png 5400w"
                alt="download the nightli app on google play"
              />
            </a>
            <a
              href="https://apps.apple.com/se/app/nightli-nightclubs-events/id6443802524"
              target="_blank"
              class="w-inline-block"
            >
              <img
                src="https://uploads-ssl.webflow.com/604e5bf6813f947d0e60c30d/604e5e1c3b8992cc86779727_apple-download-nightli.png"
                loading="lazy"
                width="150"
                sizes="(max-width: 166px) 100vw, (max-width: 767px) 200.0077362060547px, (max-width: 991px) 25vw, 205.00001525878906px"
                srcset="https://uploads-ssl.webflow.com/604e5bf6813f947d0e60c30d/604e5e1c3b8992cc86779727_apple-download-nightli-p-500.png 500w, https://uploads-ssl.webflow.com/604e5bf6813f947d0e60c30d/604e5e1c3b8992cc86779727_apple-download-nightli-p-800.png 800w, https://uploads-ssl.webflow.com/604e5bf6813f947d0e60c30d/604e5e1c3b8992cc86779727_apple-download-nightli-p-1080.png 1080w, https://uploads-ssl.webflow.com/604e5bf6813f947d0e60c30d/604e5e1c3b8992cc86779727_apple-download-nightli.png 1296w"
                alt="download the nightli app on appstore"
              />
            </a>
          </div>
          </div>
          <img
            src="https://uploads-ssl.webflow.com/604e5bf6813f947d0e60c30d/621b704ca8b443366a377c7d_nightli%20vit-09.svg"
            width="150"
            height="100"
            loading="lazy"
            alt="nightli my social nightlife app"
            class="logo-footer gh"
          />
          </div>
        </Container>
      )}

    {currentStep == 4 && (
        <Container style={{ padding: "10px" }}>
          <div className="container">
            <div className="mui--text-subhead" id="errorText">
              There is already a request for this event with email: {userInput.email}
            </div>

            <div className="mui--text-subhead">Whats next?</div>

            <div className="mui--text-subhead" id="bgWaiting">
              Wait until you recieve an email with a reply...
            </div>

            <div className="mui--text-subhead">Find more events:</div>
            <div>
            <div class="row">
              <a
                href="https://play.google.com/store/apps/details?id=se.nightli"
                target="_blank"
                class="w-inline-block"
              >
                <img
                  src="https://uploads-ssl.webflow.com/604e5bf6813f947d0e60c30d/604e5d49a8cd2b6f3ac853d8_google-download.png"
                  loading="lazy"
                  width="150"
                  sizes="(max-width: 166px) 100vw, (max-width: 767px) 200.0077362060547px, (max-width: 991px) 25vw, 205.00001525878906px"
                  srcset="https://uploads-ssl.webflow.com/604e5bf6813f947d0e60c30d/604e5d49a8cd2b6f3ac853d8_google-download-p-500.png 500w, https://uploads-ssl.webflow.com/604e5bf6813f947d0e60c30d/604e5d49a8cd2b6f3ac853d8_google-download-p-800.png 800w, https://uploads-ssl.webflow.com/604e5bf6813f947d0e60c30d/604e5d49a8cd2b6f3ac853d8_google-download-p-1080.png 1080w, https://uploads-ssl.webflow.com/604e5bf6813f947d0e60c30d/604e5d49a8cd2b6f3ac853d8_google-download-p-1600.png 1600w, https://uploads-ssl.webflow.com/604e5bf6813f947d0e60c30d/604e5d49a8cd2b6f3ac853d8_google-download-p-2000.png 2000w, https://uploads-ssl.webflow.com/604e5bf6813f947d0e60c30d/604e5d49a8cd2b6f3ac853d8_google-download-p-2600.png 2600w, https://uploads-ssl.webflow.com/604e5bf6813f947d0e60c30d/604e5d49a8cd2b6f3ac853d8_google-download.png 5400w"
                  alt="download the nightli app on google play"
                />
              </a>
              <a
                href="https://apps.apple.com/se/app/nightli-nightclubs-events/id6443802524"
                target="_blank"
                class="w-inline-block"
              >
                <img
                  src="https://uploads-ssl.webflow.com/604e5bf6813f947d0e60c30d/604e5e1c3b8992cc86779727_apple-download-nightli.png"
                  loading="lazy"
                  width="150"
                  sizes="(max-width: 166px) 100vw, (max-width: 767px) 200.0077362060547px, (max-width: 991px) 25vw, 205.00001525878906px"
                  srcset="https://uploads-ssl.webflow.com/604e5bf6813f947d0e60c30d/604e5e1c3b8992cc86779727_apple-download-nightli-p-500.png 500w, https://uploads-ssl.webflow.com/604e5bf6813f947d0e60c30d/604e5e1c3b8992cc86779727_apple-download-nightli-p-800.png 800w, https://uploads-ssl.webflow.com/604e5bf6813f947d0e60c30d/604e5e1c3b8992cc86779727_apple-download-nightli-p-1080.png 1080w, https://uploads-ssl.webflow.com/604e5bf6813f947d0e60c30d/604e5e1c3b8992cc86779727_apple-download-nightli.png 1296w"
                  alt="download the nightli app on appstore"
                />
              </a>
            </div>
            </div>
            <img
              src="https://uploads-ssl.webflow.com/604e5bf6813f947d0e60c30d/621b704ca8b443366a377c7d_nightli%20vit-09.svg"
              width="150"
              height="100"
              loading="lazy"
              alt="nightli my social nightlife app"
              class="logo-footer gh"
            />
          </div>
        </Container>
      )}
    </div>
  );
}
